/** Подключение service worker.
 *
 *  Только в собранном приложении: в разработке worker кеширует то, что
 *  через секунду изменится, и час уходит на «почему правка не видна».
 *
 *  Отказ регистрации не должен ломать ничего. Worker даёт офлайн и
 *  установку на устройство — обе вещи желанные, но приложение без них
 *  работает: в приватном окне и при запрещённых сайту хранилищах
 *  регистрация отказывает, и это нормальный день, а не происшествие. */
import { report } from './sentry';

/** Имена `DOMException`, которыми браузер САМ отказывает в регистрации —
 *  не потому, что `/sw.js` не тот, а потому, что окружение решило не
 *  давать: приватный режим, запрет хранить данные сайта, политика
 *  организации (Sentry MULT-TRAINER-A). Это НЕ поломка выкладки, и держать
 *  такое на уровне `error` значит будить разработчика на каждого
 *  посетителя в приватном окне. */
const ИМЕНА_ОТКАЗА_ОКРУЖЕНИЯ = new Set(['SecurityError', 'NotSupportedError', 'InvalidStateError']);

/** Некоторые браузеры и расширения приватности (Chrome с заблокированными
 *  данными сайта, ряд блокировщиков) отвечают на `register()` голым
 *  `Error('Rejected')` — без кода, без стека, ведущего в `/sw.js`. Три
 *  события подряд с этим текстом (MULT-TRAINER-A) — сторонний отказ ДО
 *  всякого обращения к нашему скрипту, а не его поломка. */
const СООБЩЕНИЯ_ОТКАЗА_ОКРУЖЕНИЯ = new Set(['Rejected']);

/** `SecurityError` — то самое имя из `ИМЕНА_ОТКАЗА_ОКРУЖЕНИЯ` выше, — но не
 *  всегда решение окружения. Chrome отвечает ИМ ЖЕ на НАШУ поломку выкладки:
 *  пропавший `/sw.js` площадка без правила `/assets/* → 404` отдаёт как
 *  `index.html` (`text/html`), и регистрация отказывает `SecurityError`
 *  «The script has an unsupported MIME type ('text/html')» — площадка
 *  СВОЯ, а MIME НАШ. Так же нарушение CSP `worker-src` («Refused to create
 *  a worker … Content Security Policy directive») и ошибка области
 *  регистрации («scope») — обе про то, что МЫ настроили не так, а не про
 *  решение браузера. Три слова-признака, без учёта регистра: если текст
 *  `SecurityError` содержит хоть одно — это НАША поломка (`error`), а не
 *  окружение. */
const ПРИЗНАКИ_НАШЕЙ_ПОЛОМКИ_У_SECURITY_ERROR = [/mime/i, /content security policy/i, /scope/i];

/** Отказ ОКРУЖЕНИЯ — уровень `warning`, а не `error`.
 *
 *  Список закрытый и узкий НАМЕРЕННО. Отказ, которым отвечает НАШ
 *  `/sw.js` — не скачался, ответил не 2xx, не разобрался или упал при
 *  исполнении, — у Chrome это `TypeError` с текстом вроде «a bad HTTP
 *  response code» или «An unknown error occurred when fetching the
 *  script»: под перечень ниже он не попадает и остаётся `error`, потому
 *  что это поломка выкладки, о которой надо знать. */
export function отказОкружения(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (
    error.name === 'SecurityError'
    && ПРИЗНАКИ_НАШЕЙ_ПОЛОМКИ_У_SECURITY_ERROR.some((признак) => признак.test(error.message))
  ) {
    return false;
  }
  return ИМЕНА_ОТКАЗА_ОКРУЖЕНИЯ.has(error.name) || СООБЩЕНИЯ_ОТКАЗА_ОКРУЖЕНИЯ.has(error.message);
}

export function registerServiceWorker(): void {
  if (!import.meta.env.PROD) return;
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch((error: unknown) => {
      /** Занятия от этого не ломаются — ломаются два обещания, и оба
       *  записаны в документах, которые родитель читает: приложение ставится
       *  на домашний экран и открывается без сети.
       *
       *  Молчать тут значит не узнать о самой вероятной причине — что
       *  `/sw.js` перестал отдаваться после правки сборки. Это тихая
       *  поломка ровно того вида, против которого весь проект и построен:
       *  на экране всё хорошо, событий нет, потому что их некому слать. */
      report(error, 'registerServiceWorker', отказОкружения(error) ? 'warning' : 'error');
    });
  });
}

export type InstallHint = 'none' | 'ios' | 'prompt';

/** Какое предложение установки показывать.
 *
 *  Вынесено функцией, потому что развилка неочевидна и её надо проверять
 *  прогоном. На iOS системного предложения установки НЕ СУЩЕСТВУЕТ —
 *  события `beforeinstallprompt` там нет, и ждать его бессмысленно. А мотив
 *  всей установки именно iOS: без неё Safari стирает хранилище после семи
 *  дней без захода. Значит там нужна прямая инструкция человеку, а не
 *  кнопка.
 *
 *  Уже установленному не показываем ничего: он и так внутри. */
export function installHint(
  { standalone, ios, promptAvailable, dismissed }:
  { standalone: boolean; ios: boolean; promptAvailable: boolean; dismissed: boolean },
): InstallHint {
  if (standalone || dismissed) return 'none';
  if (promptAvailable) return 'prompt';
  return ios ? 'ios' : 'none';
}

/** Приложение уже открыто как установленное. */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches
    || (window.navigator as { standalone?: boolean }).standalone === true
  );
}

/** iOS и iPadOS. Второе условие — про iPad, который с некоторых пор
 *  представляется Макинтошем и отличается только наличием касаний. */
export function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua)
    || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}
